import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/queryKeys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

interface RateRow {
  from_currency: string;
  to_currency: string;
  rate: number;
  updated_at: string;
}

export function RatesPage() {
  const { t, i18n } = useTranslation();
  const ratesQ = useQuery({
    queryKey: qk.rates,
    queryFn: () => callFunction<{ rates: RateRow[] }>('rates-list'),
  });

  if (ratesQ.isLoading) return <LoadingScreen />;
  if (ratesQ.isError) return <ErrorState onRetry={() => ratesQ.refetch()} />;
  if (!ratesQ.data?.rates.length) return <EmptyState title={t('rates.noRates')} />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('rates.title')}</CardTitle>
      </CardHeader>
      <ul className="flex flex-col gap-2 text-sm">
        {ratesQ.data.rates.map((r) => (
          <li
            key={`${r.from_currency}-${r.to_currency}`}
            className="flex items-center justify-between rounded-md border border-border bg-surface-elevated px-4 py-3"
          >
            <span className="font-mono">
              {t('rates.perUnit', { from: r.from_currency, rate: r.rate, to: r.to_currency })}
            </span>
            <span className="text-xs text-text-muted">
              {t('rates.lastUpdated', {
                when: new Date(r.updated_at).toLocaleString(
                  i18n.language === 'fr' ? 'fr-FR' : 'en-US',
                ),
              })}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
